import React, { useState } from 'react';
import SearchableDropdown from '../SearchableDropdown';
import {
  CATEGORY_LABELS,
  type DocumentCategory,
  type DocumentTemplate,
} from '../../services/staffDocumentService';

// Port of platform-app components/team/AcknowledgmentEditor.tsx (RUBY light
// chrome). AI generation / voice dictation stripped — pos-agents is
// tenant-side; `canUseAI` is accepted for prop-contract compatibility but
// ignored.

interface AcknowledgmentSavePayload {
  templateType: 'acknowledgment';
  name: string;
  description?: string;
  category: DocumentCategory;
  disclaimerText: string;
  isRequired: boolean;
  requiresSignature: boolean;
  requireTypedName: boolean;
  allowAttachResults: boolean;
  acceptedTypes: string[];
}

interface AcknowledgmentEditorProps {
  initial?: Partial<DocumentTemplate>;
  onSave: (payload: AcknowledgmentSavePayload) => void;
  onCancel: () => void;
  /** Ignored — AI features are not available in the staff portal. */
  canUseAI?: boolean;
}

const CATEGORY_OPTIONS = (Object.keys(CATEGORY_LABELS) as DocumentCategory[]).map((value) => ({
  value,
  label: CATEGORY_LABELS[value],
}));

export default function AcknowledgmentEditor({
  initial,
  onSave,
  onCancel,
}: AcknowledgmentEditorProps) {
  const [name, setName] = useState<string>(initial?.name ?? '');
  const [description, setDescription] = useState<string>(initial?.description ?? '');
  const [category, setCategory] = useState<DocumentCategory>(
    (initial?.category as DocumentCategory) ?? 'other'
  );
  const [disclaimerText, setDisclaimerText] = useState<string>(initial?.disclaimerText ?? '');
  const [isRequired, setIsRequired] = useState<boolean>(initial?.isRequired ?? true);
  const [requiresSignature, setRequiresSignature] = useState<boolean>(
    (initial as any)?.requiresSignature ?? true
  );
  const [requireTypedName, setRequireTypedName] = useState<boolean>(
    (initial as any)?.requireTypedName ?? true
  );
  const [allowAttachResults, setAllowAttachResults] = useState<boolean>(
    (initial as any)?.allowAttachResults ?? false
  );

  const [showValidation, setShowValidation] = useState(false);

  const nameError = name.trim() === '';
  const textError = disclaimerText.trim() === '';

  const handleSave = () => {
    if (nameError || textError) {
      setShowValidation(true);
      return;
    }
    onSave({
      templateType: 'acknowledgment',
      name: name.trim(),
      description: description.trim() ? description.trim() : undefined,
      category,
      disclaimerText: disclaimerText.trim(),
      isRequired,
      requiresSignature,
      requireTypedName,
      allowAttachResults,
      acceptedTypes: [],
    });
  };

  const inputClass =
    'w-full px-3 py-2 border border-stone-300 rounded-lg text-sm text-stone-900 bg-white placeholder-stone-400 focus:outline-none focus:border-admin-primary';

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {/* ===================== LEFT: EDITOR ===================== */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-5 space-y-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">
            Template Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Background Check Authorization"
            className={inputClass}
          />
          {showValidation && nameError && (
            <p className="text-[11px] text-red-600 mt-1">Template name is required.</p>
          )}
        </div>

        {/* Description (optional) */}
        <div>
          <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">
            Description <span className="text-stone-400 normal-case font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short summary shown to admins"
            className={inputClass}
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">
            Category
          </label>
          <SearchableDropdown
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={(v) => setCategory((v as DocumentCategory) || 'other')}
            placeholder="Select category"
            searchPlaceholder="Search categories..."
          />
        </div>

        {/* Agreement / Policy Text */}
        <div>
          <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">
            Agreement / Policy Text
          </label>
          <textarea
            rows={6}
            value={disclaimerText}
            onChange={(e) => setDisclaimerText(e.target.value)}
            placeholder="Enter the text the staff member must read and agree to…"
            className={inputClass}
          />
          {showValidation && textError && (
            <p className="text-[11px] text-red-600 mt-1">Agreement text is required.</p>
          )}
        </div>

        {/* Toggles row 1 */}
        <div className="flex items-center gap-6 text-sm text-stone-700">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requiresSignature}
              onChange={(e) => setRequiresSignature(e.target.checked)}
              className="accent-admin-primary"
            />
            Require signature
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requireTypedName}
              onChange={(e) => setRequireTypedName(e.target.checked)}
              className="accent-admin-primary"
            />
            Require typed full name
          </label>
        </div>

        {/* Toggles row 2 */}
        <div className="flex items-center gap-6 text-sm text-stone-700">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="accent-admin-primary"
            />
            Required document
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowAttachResults}
              onChange={(e) => setAllowAttachResults(e.target.checked)}
              className="accent-admin-primary"
            />
            Allow admin to attach results later
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-sm text-stone-500 hover:text-stone-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="bg-admin-primary hover:bg-admin-secondary text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          >
            Save Template
          </button>
        </div>
      </div>

      {/* ===================== RIGHT: LIVE PREVIEW ===================== */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-stone-500 uppercase">Staff member sees</p>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-stone-900">
            {name.trim() || 'Untitled Acknowledgment'}
          </h3>
          <p className="text-xs text-stone-600 mt-2 leading-relaxed whitespace-pre-wrap">
            {disclaimerText.trim() || 'The agreement text the staff member reads will appear here…'}
          </p>
          <label className="flex items-center gap-2 mt-3 text-sm text-stone-700">
            <input type="checkbox" disabled className="accent-admin-primary" /> I have read and agree
          </label>
          {requireTypedName && (
            <input
              type="text"
              disabled
              placeholder="Type your full name to sign"
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm text-stone-900 bg-white placeholder-stone-400 mt-2"
            />
          )}
          <button
            type="button"
            disabled
            className="mt-3 bg-admin-primary text-white px-4 py-2 rounded-lg text-sm font-semibold w-full opacity-90 cursor-default"
          >
            Acknowledge &amp; Sign
          </button>
        </div>
      </div>
    </div>
  );
}
